/**
 * Input devices: the boundary between the physical world and the simulation.
 *
 * An `InputDevice` reports two kinds of thing:
 *   - button transitions (a switch, a foot pedal, a wrist-mounted contact, a
 *     key standing in for one during development)
 *   - EMG samples (a raw or pre-enveloped signal from a muscle electrode)
 *
 * It reports what happened. It never decides what it means -- that is the
 * controller's job -- and it has no idea a game exists.
 *
 * Nothing in `game/` may import from this directory.
 */

export type InputDeviceStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface ButtonEvent {
  /** true = pressed (BUTTON_DOWN), false = released (BUTTON_UP). */
  down: boolean;
  /** performance.now() at detection, ms. */
  timestamp: number;
  /** Device id that produced the event. */
  source: string;
  /**
   * Correlation id, when the device knows which stimulation request this
   * response belongs to. Simulated devices always know; a real switch never
   * does, and attribution is done by the controller instead.
   */
  requestId?: string;
  metadata?: Record<string, unknown>;
}

export interface EmgSample {
  /** Raw or pre-enveloped amplitude, device units. */
  value: number;
  timestamp: number;
  channel: number;
  source: string;
}

export interface InputDevice {
  readonly id: string;
  readonly label: string;
  /** True if this device can produce EMG samples as well as button events. */
  readonly providesEmg: boolean;

  connect(): Promise<void>;
  disconnect(): Promise<void>;
  getStatus(): InputDeviceStatus;

  onButton(fn: (e: ButtonEvent) => void): () => void;
  onEmg(fn: (e: EmgSample) => void): () => void;

  /** Free-form diagnostics for the dashboard. */
  info?(): Record<string, unknown>;
}

/** Listener plumbing and status bookkeeping shared by every implementation. */
export abstract class BaseInputDevice implements InputDevice {
  abstract readonly id: string;
  abstract readonly label: string;
  readonly providesEmg: boolean = false;

  protected status: InputDeviceStatus = 'disconnected';
  protected lastError: string | null = null;

  private buttonListeners = new Set<(e: ButtonEvent) => void>();
  private emgListeners = new Set<(e: EmgSample) => void>();

  abstract connect(): Promise<void>;
  abstract disconnect(): Promise<void>;

  getStatus(): InputDeviceStatus {
    return this.status;
  }

  onButton(fn: (e: ButtonEvent) => void): () => void {
    this.buttonListeners.add(fn);
    return () => this.buttonListeners.delete(fn);
  }

  onEmg(fn: (e: EmgSample) => void): () => void {
    this.emgListeners.add(fn);
    return () => this.emgListeners.delete(fn);
  }

  info(): Record<string, unknown> {
    return { status: this.status, error: this.lastError };
  }

  protected emitButton(e: ButtonEvent): void {
    for (const fn of this.buttonListeners) fn(e);
  }

  protected emitEmg(e: EmgSample): void {
    for (const fn of this.emgListeners) fn(e);
  }
}

/**
 * Debounce / edge conditioner.
 *
 * Mechanical switches bounce for a few milliseconds and a contact taped to a
 * twitching wrist bounces for longer. Without this a single arm movement can
 * register as three flaps, which would silently inflate the "successful
 * command" rate. The FIRST edge is reported (that is the real response time);
 * subsequent edges inside the window are swallowed.
 */
export class Debouncer {
  private lastDownAt = -Infinity;
  private lastUpAt = -Infinity;
  private isDown = false;

  constructor(public debounceMs = 25) {}

  /** @returns true if this transition should be reported. */
  accept(down: boolean, timestamp: number): boolean {
    if (down) {
      if (this.isDown) return false;
      if (timestamp - this.lastDownAt < this.debounceMs) return false;
      this.lastDownAt = timestamp;
      this.isDown = true;
      return true;
    }
    if (!this.isDown) return false;
    if (timestamp - this.lastUpAt < this.debounceMs) return false;
    this.lastUpAt = timestamp;
    this.isDown = false;
    return true;
  }

  reset(): void {
    this.lastDownAt = -Infinity;
    this.lastUpAt = -Infinity;
    this.isDown = false;
  }
}
